import AuthModal from "@/components/auth/AuthModal";
import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import SearchBar from "@/components/search/SearchBar";
import { Button } from "@/components/ui/button";
import { SearchProvider } from "@/context/SearchContext";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  Brain,
  CheckCircle2,
  Clock,
  Cloud,
  DollarSign,
  Gauge,
  Infinity,
  LockKeyhole,
  LockKeyholeIcon,
  Rocket,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Target,
  ThumbsUp,
  TrendingDown,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import nextgen from "../assets/images/brand_logo.webp";
import fulcrum from "../assets/images/fulcrum.png";
import growel from "../assets/images/growel.webp";
import nilkamal from "../assets/images/nilkamal.png";
import NebulaSphereCanvas from "../forgetPassword/NebulaSphereCanvas";
// import nextgen from "../assets/images/nextgen.webp";
import bannerimg from "../assets/images/banner.png";
import fanstudio from "../assets/images/fanstudio.png";
import maxipitchflow from "../assets/images/maxipitchflow.png";
import step_1 from "../assets/images/steps/step_1.png";
import step_2 from "../assets/images/steps/step_2.png";
import step_3 from "../assets/images/steps/step_3.webp";
import TAC from "../assets/images/TAC_Security.webp";
import zikol from "../assets/images/zikol.png";
import { CasaIcon, CcpaIcon, GdprIcon, Soc2Icon } from "../utils/securityIcons";
import CRMSectionWidget from "../widgets/CRMSectionWidget";
import IntegrationToolsWidget from "../widgets/IntegrationToolsWidget";
import ProductVideoSection from "../widgets/ProductVideoSection";
import MaxiPitchLoader from "/src/widgets/CustomLoaderWidget.jsx";
import ComparisonIllustration from "./ComparisonIllustration";
import { fetchSubscriptions } from "../Redux/ReduxSlices/priceSlice";
import Meta from "../components/Meta/Meta";
// import StickyFeatureSection from "./StickyFeatureSection";
import TestimonialGrid from "./testimonial/CustomerTestimonialPage";
// import AuraSliderSection from "./AuraSliderSection";

export default function HomePage({ isAuthenticated }) {
  const { scrollYProgress } = useScroll();
  const [showLoader, setShowLoader] = useState(false);

  // Apple-like cinematic transforms
  const y = useTransform(scrollYProgress, [0, 1], [20, -160]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [0.72, 1.08]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState("signup");
  const { sessionData, loading, error } = useSelector(
    (state) => state.appoloAPi,
  );
  const { subscriptions, plansloading } = useSelector((state) => state.pricing);

  const [isVisible, setIsVisible] = useState(false);
  const sectionRefs = useRef([]);
  const [openWaitingListModal, setOpenWaitingListModal] = useState(false);
  const openAuthModal = (tab) => {
    setAuthModalTab(tab);
    setAuthModalOpen(true);
  };

  useEffect(() => {
    dispatch(fetchSubscriptions());
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const countryName = localStorage.getItem("user_country_name") || "India";

  const isIndia = countryName.toLowerCase() === "india";
  const currency = isIndia ? "INR" : "USD";

  const selectedPlan = useMemo(() => {
    if (!subscriptions?.data?.items) return null;

    return (
      subscriptions.data.items.find(
        (p) => p.currency.toUpperCase() === currency,
      ) || null
    );
  }, [subscriptions, currency]);

  useEffect(() => {
    const key = "maxipitch_loader_seen";
    const hasSeen = localStorage.getItem(key) === "true";

    if (!hasSeen) {
      setShowLoader(true);
      localStorage.setItem(key, "true");

      // loader duration: match your animation end (~3.2s)
      const t = setTimeout(() => setShowLoader(false), 4000);
      return () => clearTimeout(t);
    }
  }, []);

  const trustPoints = [
    "Personalized emails go out automatically",
    "Follow-ups run without manual work",
    "Replies are tracked in real time",
    "Campaign performance is always visible",
    "Everything flows into a single automated engine",
  ];

  const colorStyles = {
    blue: {
      bg: "bg-blue-100",
      iconBg: "bg-blue-100",
      iconText: "text-blue-600",
      dot: "bg-blue-600",
    },
    purple: {
      bg: "bg-purple-100",
      iconBg: "bg-purple-100",
      iconText: "text-purple-600",
      dot: "bg-purple-600",
    },
    green: {
      bg: "bg-green-100",
      iconBg: "bg-green-100",
      iconText: "text-green-600",
      dot: "bg-green-600",
    },
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in");
          }
        });
      },
      { threshold: 0.1 },
    );

    const currentRefs = sectionRefs.current;
    currentRefs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      currentRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  const scrollToSearch = () => {
    searchRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const topLogos = [
    { name: "Growell Softtech", src: growel },
    { name: "Fulcrum", src: fulcrum },
    { name: "Fanstudio ", src: fanstudio },
    { name: "Next Gen", src: nextgen },
  ];

  const bottomLogos = [
    { name: "Zikol ", src: zikol },
    { name: "Nilkamal", src: nilkamal },
  ];

  const handleGetStarted = () => {
    const token = localStorage.getItem("token");

    if (token) {
      navigate("/pricing"); // user already logged in
    } else {
      openAuthModal("signup"); // user not logged in → go to lead form
    }
  };

  if (showLoader || !selectedPlan || plansloading) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <MaxiPitchLoader />
      </div>
    );
  }

  return (
    <SearchProvider>
      <Meta
        title="AI Sales Automation Software | MaxiPitch Lead Generation Tool"
        description="MaxiPitch is an AI sales automation software for lead generation, automated outreach, and faster business growth with smart marketing tools."
        url="https://maxipitch.com/"
      />
      <div className="min-h-screen flex flex-col bg-white relative ">
        <Header />

        <main className="flex-grow relative z-10">
          {/* Hero Section */}
          <section className="py-14 max-md:py-8   relative overflow-hidden">
            <div className="absolute inset-0 z-0">
              <img
                src="/images/pattern-1.svg"
                alt="Background"
                className="w-full h-full object-cover opacity-25"
              />

              {/* Top Fade Blur */}
              <div
                className="pointer-events-none absolute top-0 left-0 w-full h-28 
                  bg-gradient-to-b from-white via-white/80 to-transparent 
                  backdrop-blur-md"
              />

              {/* Bottom Fade Blur */}
              <div
                className="pointer-events-none absolute bottom-0 left-0 w-full h-28 
                  bg-gradient-to-t from-white via-white/80 to-transparent 
                  backdrop-blur-md"
              />
            </div>
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
              <div className="absolute top-20 left-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
              <div className="absolute top-0 right-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
              <div className="absolute -bottom-8 left-20 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
            </div>

            <div className="container mx-auto px-4 text-center relative z-10">
              <div className="mb-4">
                <div className="inline-flex items-center rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2 text-sm font-medium text-white mb-6 animate-pulse-slow">
                  <Sparkles className="h-4 w-4 mr-2" />
                  AI-Powered Lead Generation
                </div>
                <h1 className="">
                  Get New Customers. Every Day.
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                    {" "}
                    Automatically.
                  </span>
                </h1>
                <p className="text-lg max-md:text-sm text-gray-500 max-w-3xl mx-auto mb-4 leading-relaxed">
                  Tell us who your ideal customer is. We find the perfect
                  companies, contact the right people, and help you send
                  personalized emails from your own email ID live in just 2-3
                  clicks
                </p>
              </div>

              <div className="mb-4 max-md:mb-6 flex justify-center items-center text-center">
                <p className="px-10 py-2 pb-8 max-md:pb-2 text-blue-600 text-base font-medium whitespace-nowrap max-md:whitespace-normal max-md:text-xs">
                  Try a Free Search | No Signup Required |
                  <span className="block md:inline">
                    {" "}
                    No Credit Card Required
                  </span>
                </p>
              </div>

              <div
                ref={searchRef}
                className="max-w-4xl mx-auto mb-12 transform transition-all duration-500 hover:scale-105"
              >
                <div className="bg-white border border-gray-200 rounded-2xl p-8 max-md:p-4 shadow-2xl relative">
                  {/* gradient overlay */}
                  <div
                    className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-300 
                    rounded-2xl opacity-20 blur pointer-events-none"
                  ></div>
                  <SearchBar />
                </div>
              </div>

              <div className="pt-10 max-md:pt-4 text-center">
                <div className="flex items-center justify-center gap-4 mb-3">
                  <div className="h-px w-16 bg-gray-300" />
                  <span className="text-sm uppercase tracking-wider text-indigo-500">
                    Used by
                  </span>
                  <div className="h-px w-16 bg-gray-300" />
                </div>

                <p className="text-xl max-md:text-sm text-gray-800 max-w-4xl mx-auto">
                  Small business owners, IT services, exporters, manufacturers,
                  SaaS teams and more
                </p>
              </div>

              {/* <div className="flex flex-col items-center">
                <Button
                  size="lg"
                  className="relative bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl mb-4 group overflow-hidden"
                  onClick={() => openAuthModal("signup")}
                >
                  <span className="relative z-10">
                    Start Generating Leads - Free
                  </span>
                  <ArrowRight className="ml-2 h-5 w-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                  <div className="absolute inset-0 h-full w-full scale-0 rounded-lg transition-all duration-300 group-hover:scale-100 group-hover:bg-white/30"></div>
                </Button>
                <p className="text-sm text-gray-600">
                  * No Credit Card Required
                </p>
              </div> */}
            </div>
          </section>
          <section>
            <div className="mt-8 max-md:mt-10 relative overflow-hidden">
              <motion.h2
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-[20px] md:text-3xl font-bold text-slate-900 text-center relative z-10 pt-4"
              >
                Our Security Partner
                <br className="hidden sm:block" />
              </motion.h2>

              <div className="flex items-center justify-center px-8 max-md:px-4 relative z-10 pb-6">
                <img
                  src={TAC}
                  alt={"TAC logo"}
                  className="h-24 max-md:h-14 w-auto object-contain select-none"
                  loading="lazy"
                  draggable={false}
                />
              </div>

              {/* SVG Background with fade on left & right */}
              <div className="absolute inset-0 pointer-events-none">
                <svg
                  className="w-full h-full opacity-[0.2]"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    maskImage:
                      "linear-gradient(to right, transparent 0%, black 20%, black 85%, transparent 100%)",
                    WebkitMaskImage:
                      "linear-gradient(to right, transparent 0%, black 25%, black 85%, transparent 100%)",
                  }}
                >
                  <defs>
                    <pattern
                      id="itw-grid"
                      width="52"
                      height="52"
                      patternUnits="userSpaceOnUse"
                    >
                      <path
                        d="M 48 0 L 0 0 0 48"
                        fill="none"
                        stroke="rgb(99 102 241)"
                        strokeWidth="1"
                      />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#itw-grid)" />
                </svg>
              </div>
            </div>
          </section>
          <ProductVideoSection />

          {/* <ComparisonIllustration /> */}
          <section className="pt-12 pb-8 relative max-md:py-8 max-md:pb-0 overflow-hidden ">
            <div className="container mx-auto px-4 relative z-10">
              <div
                className="text-center relative"
                ref={(el) => (sectionRefs.current[0] = el)}
              >
                <h2 className="">How It Works In 3 Simple Steps</h2>
                <p className="text-xl max-md:text-[16px] text-gray-600 max-w-2xl mx-auto mt-5">
                  Setup your email campaign in less than 5 minutes
                </p>
                {/* Curved underline (full width + two lines) */}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-md:gap-0 relative z-10">
                {[
                  {
                    image: step_1,
                    color: "blue",
                    title:
                      "1. Tell us who you want to sell to & we’ll find the right people for your product or service.",
                    desc: "Example: Purchase Managers at hotels & resorts in India / CTO's of SaaS companies",
                  },
                  {
                    image: step_2,
                    color: "purple",
                    title:
                      "2. Securely connect your email. We work seamlessly with all major providers.",
                    desc: "Gmail, Zoho, Outlook, SMTP",
                  },
                  {
                    image: step_3,
                    color: "green",
                    title:
                      "3. Our AI writes your emails, and we take care of sending them.",
                    desc: "50 targeted emails go out daily from your mailbox. Replies land in your inbox",
                  },
                ].map((step, index) => (
                  <div
                    key={index}
                    className="text-center p-6 max-md:p-4 group flex flex-col max-md:pt-12 pt-14"
                    ref={(el) => (sectionRefs.current[index + 1] = el)}
                  >
                    {/* Text always first on mobile, but order changes on desktop */}
                    <div className="order-1 md:order-2">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {step.title}
                      </h4>
                      <p className="text-gray-600">{step.desc}</p>
                    </div>

                    {/* Image always below text on mobile, above on desktop */}
                    <div className="order-2 md:order-1">
                      <div className="flex justify-center items-center mt-6 md:mt-0 md:mb-6 h-[300px] max-md:h-[240px]">
                        <img
                          src={step.image}
                          alt={`Step ${index + 1}`}
                          className="w-full max-w-[300px] h-auto max-h-[280px] object-contain transition-transform duration-300 group-hover:scale-105"
                          style={{ imageRendering: "crisp-edges" }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="py-8 max-md:py-6 relative z-10">
                <div className="flex justify-center items-center">
                  <Button
                    className="main-button rounded-md text-base max-md:text-sm font-semibold max-md:px-8 bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 transition-colors duration-300 shadow-md hover:shadow-lg"
                    size="lg"
                    onClick={scrollToSearch}
                  >
                    Try Free Search
                  </Button>
                </div>
              </div>
            </div>
          </section>
          {/* <StickyFeatureSection /> */}
          <CRMSectionWidget />
          <section className="relative py-8 max-md:py-0  mb-12 overflow-hidden max-md:mb-16 max-md:px-4">
            <div className="absolute top-10 left-10 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-1000"></div>

            <div className="max-w-7xl mx-auto ">
              <div className="relative flex flex-col lg:flex-row items-center gap-24 max-md:gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className="lg:w-[55%] relative z-10"
                >
                  <h2 className=" max-md:text-center">
                    Where Smart
                    <span className="text-blue-600 ml-2 ">Automation</span>
                    <br />
                    Meets Simple Execution
                  </h2>

                  <p className="mt-6 text-lg text-gray-600 leading-relaxed max-md:text-base max-md:text-center">
                    Advanced AI scans millions of data points to find ideal
                    customers. Only the highest-quality, best-matched leads are
                    selected. All company and contact information is
                    continuously verified. Predictive lead scoring ranks
                    customers by conversion potential.
                  </p>

                  <div className="mt-10 max-md:mt-8 space-y-4">
                    {trustPoints.map((t, i) => (
                      <div
                        key={i}
                        className="flex items-center max-md:justify-center gap-3"
                      >
                        <CheckCircle2 className="h-5 w-5 text-blue-500 mt-1 max-md:mt-0" />
                        <span className="text-gray-700 text-base font-semibold max-md:text-xs">
                          {t}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                <div className="lg:w-[65%] relative overflow-visible ">
                  <motion.img
                    src={maxipitchflow}
                    alt="Product UI"
                    className="
                      w-full 
                      max-w-[500px] 
                      sm:max-w-[600px] 
                      lg:max-w-none   
                      relative 
                      lg:absolute 
                      lg:top-1/2 
                      lg:-translate-y-1/2
                      select-none 
                      pointer-events-none
                    "
                  />
                </div>
              </div>
            </div>
          </section>
          <div className="w-full absolute">
            <div className="h-[430px] max-md:h-[390px]">
              <NebulaSphereCanvas particleCount={3000} />
            </div>
          </div>

          <section className="py-12 max-md:py-2 pt-18  relative overflow-hidden">
            <div className="container mx-auto px-4 relative z-10">
              <div className="text-center mb-20 max-md:mb-12">
                <h2 className="text-[27px] md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                  Why Businesses{" "}
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent ">
                    {" "}
                    Pick MaxiPitch
                  </span>
                </h2>
                <img
                  src={bannerimg}
                  alt="curved underline"
                  className=" absolute top-9
                            mx-auto
                            w-full
                            h-7
                            pl-6
                            object-contain
                            max-md:hidden
                          "
                />

                <p className="text-xl pt-8 max-md:pt-1 pb-32 max-md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  Stop wasting time on complicated tools. MaxiPitch gives you
                  real outreach results in minutes, not hours.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20 max-md:mb-10 max-md:p-0">
                {[
                  {
                    icon: Zap,
                    color: "blue-500",
                    gradient: "from-blue-500 to-cyan-500",
                    title: "5-Minute Setup",
                    dotColor: "bg-blue-500",
                    desc: "Made for busy owners. No tech skills needed.",
                    stats: "5 min",
                    featureList: [
                      "No installation",
                      "Simple dashboard",
                      "Send emails in 1 click",
                    ],
                  },
                  {
                    icon: Target,
                    color: "green-500",
                    gradient: "from-emerald-500 to-green-500",
                    title: "Real Outreach",
                    dotColor: "bg-green-500",
                    desc: "Reach decision-makers, not spam folders.",
                    stats: "98% deliverability",
                    featureList: [
                      "Clean email lists",
                      "Personalized copy",
                      "Human-like messaging",
                    ],
                  },
                  {
                    icon: Shield,
                    color: "purple-500",
                    gradient: "from-purple-500 to-pink-500",
                    title: "Zero Risk Start",
                    dotColor: "bg-purple-500",
                    desc: "Try before you commit. Always transparent.",
                    stats: "Free trial",
                    featureList: [
                      "50 free emails",
                      "Free search",
                      "No card required",
                    ],
                  },
                  {
                    icon: LockKeyhole,
                    color: "orange-500",
                    gradient: "from-amber-500 to-orange-500",
                    title: "Your Data, Always",
                    dotColor: "bg-orange-500",
                    desc: "Full ownership & control. GDPR compliant.",
                    stats: "100% owned",
                    featureList: [
                      "Watch analytics anytime",
                      "Data encrypted",
                      "You own replies",
                    ],
                  },
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="group relative"
                    //
                  >
                    <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 from-blue-100/50 via-purple-100/30 to-pink-100/20 rounded-3xl blur-xl"></div>

                    <div className="relative bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-2xl px-8 pt-4 h-full shadow-lg hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2 hover:border-gray-300/50 overflow-hidden">
                      <div
                        className={`absolute top-0 right-0 w-16 h-16 overflow-hidden`}
                      >
                        <div
                          className={`absolute -top-8 -right-8 w-16 h-16 bg-gradient-to-br ${feature.gradient} opacity-10 group-hover:opacity-20 transition-opacity duration-500`}
                        ></div>
                      </div>

                      <div className="relative z-10 mb-6">
                        <div
                          className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${feature.gradient} shadow-lg group-hover:scale-110 transition-transform duration-500`}
                        >
                          <feature.icon
                            className="h-8 w-8 max-md:h-4 max-md:w-4 text-white"
                            strokeWidth={1.9}
                          />
                        </div>

                        <div className="absolute -top-2 -right-2">
                          <span className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm font-bold text-gray-700 shadow-sm">
                            {feature.stats}
                          </span>
                        </div>
                      </div>

                      <h3 className="">{feature.title}</h3>

                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {feature.desc}
                      </p>

                      <ul className="space-y-3 mb-6">
                        {feature.featureList.map((item, idx) => (
                          <li
                            key={idx}
                            className="flex items-center gap-3 text-gray-700"
                          >
                            <div
                              className={`w-1.5 h-1.5 rounded-full ${feature.dotColor}`}
                            ></div>

                            <span className="text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent group-hover:via-gray-300 transition-all duration-500"></div>
                    </div>

                    {index < 3 && (
                      <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-gray-300 to-transparent group-hover:from-blue-300 transition-colors duration-300"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
          {/* <AuraSliderSection /> */}

          {/* <section className="py-18 px-20 max-md:px-1 mt-2">
            <TestimonialGrid />
          </section> */}
          {/* Security & Compliance Micro-section */}
          <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
            <div className="max-w-6xl mx-auto px-4 text-center">
              {/* Heading */}
              <h2 className="font-bold text-gray-900 mb-4">
                Bank-Level <span className="text-blue-600">Security</span> For
                Your Peace Of Mind
              </h2>

              <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
                Your data is protected with enterprise-grade infrastructure and
                privacy-first principles.
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
                {/* Item */}
                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                  <ShieldAlert className="h-8 w-8 text-blue-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-gray-900 mb-2">
                    SOC 2 Type 1
                  </h4>
                  <p className="text-sm text-gray-600">
                    Independently audited for security, availability, and
                    confidentiality.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                  <LockKeyholeIcon className="h-8 w-8 text-indigo-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Encryption
                  </h4>
                  <p className="text-sm text-gray-600">
                    Emails are encrypted both in transit and at rest.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                  <Cloud className="h-8 w-8 text-purple-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Enterprise Infrastructure
                  </h4>
                  <p className="text-sm text-gray-600">
                    Secure cloud with 99.9% uptime reliability.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                  <ShieldCheck className="h-8 w-8 text-green-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Privacy First
                  </h4>
                  <p className="text-sm text-gray-600">
                    We never train AI models on your data. You own everything.
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div className=" flex flex-col justify-center items-center border-t border-gray-200 pt-10">
                {/* Badges */}
                <div className="grid grid-cols-2 md:grid-cols-4 justify-items-center items-center gap-x-6 gap-y-2 mb-6 max-w-[400px]">
                  <Soc2Icon className="inline-block flex-grow-0 flex-shrink-0 text-inherit max-sm:size-24 size-16 sm:w-[96px] sm:h-[96px]" />
                  <CasaIcon className="inline-block flex-grow-0 flex-shrink-0 text-inherit max-sm:size-24 size-16 sm:w-[96px] sm:h-[96px]" />
                  <GdprIcon className="inline-block flex-grow-0 flex-shrink-0 text-inherit max-sm:size-24 size-16 sm:w-[96px] sm:h-[96px]" />
                  <CcpaIcon className="inline-block flex-grow-0 flex-shrink-0 text-inherit max-sm:size-24 size-16 sm:w-[96px] sm:h-[96px]" />
                </div>
                {/* Bottom Text */}
                <h3 className="text-lg font-semibold text-gray-900">
                  Secure. Private. Encrypted.
                </h3>

                <p className="text-sm text-gray-600 mt-2 max-w-xl mx-auto">
                  With CASA Tier 3 compliance and industry-leading encryption,
                  your emails stay private, secure, and protected—always.
                </p>
              </div>
            </div>
          </section>
          {/* <section className="w-full bg-white pb-14 max-md:pb-0">
            <div className="mx-auto max-w-6xl">
              <h2 className="mb-12 max-md:mb-4 max-md:px-8 mt-16 max-md:mt-16 text-center text-[28px] md:text-4xl font-bold text-gray-900 leading-tight">
                Trusted By Hundreds Of
                <span className="ml-3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Users At Companies Like
                </span>
              </h2>

              <div className="relative mx-auto max-w-3xl">
                <div className="pointer-events-none absolute inset-0 hidden md:block">
                  <span className="absolute left-1/4 top-0 h-full w-px bg-sky-200/70" />
                  <span className="absolute left-2/4 top-0 h-full w-px bg-sky-200/70" />
                  <span className="absolute left-3/4 top-0 h-full w-px bg-sky-200/70" />
                  <span className="absolute left-0 top-1/2 h-px w-full bg-sky-200/70" />
                </div>

                <div className="grid grid-rows-2 ">
                  <div className="grid grid-cols-2 md:grid-cols-4">
                    {topLogos.map((logo) => (
                      <div
                        key={logo.name}
                        className="flex max-md:h-20 items-center justify-center"
                      >
                        <img
                          src={logo.src}
                          alt={logo.name}
                          className="h-10 md:h-12  opacity-60 grayscale"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4">
                    <div className="hidden md:block" />
                    {bottomLogos.map((logo) => (
                      <div
                        key={logo.name}
                        className="flex h-24 max-md:h-24 items-center justify-center"
                      >
                        <img
                          src={logo.src}
                          alt={logo.name}
                          className="h-8 md:h-10 opacity-60 grayscale"
                        />
                      </div>
                    ))}
                    <div className="hidden md:block" />
                  </div>
                </div>
              </div>
            </div>
          </section> */}
          {/* {Pricing section} */}
          <section className="py-12 max-md:py-8 bg-white mt-12 max-md:mt-8">
            <div className="container mx-auto px-4">
              {/* Heading */}
              <div className="text-center max-w-4xl mx-auto">
                <h2 className="">
                  Hire The World’s Smartest AI Sales Agent For
                  <span className="">
                    <span className="text-indigo-600">
                      {" "}
                      Only{" "}
                      <span className=" ml-1 mr-1">
                        {" "}
                        {selectedPlan.currency === "USD" ? "$" : "₹"}{" "}
                      </span>
                      {selectedPlan.price}
                      <span className="text-lg">/Month</span>
                    </span>
                  </span>
                </h2>
                <p className="mt-3 text-slate-600 text-sm md:text-base">
                  The ultimate sales powerhouse for your products and services
                </p>
              </div>

              {/* Pricing cards */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
                {/* FREE PLAN */}
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  whileHover={{ scale: 1.03 }}
                  className="rounded-2xl border border-slate-200 bg-white hover:shadow-md transition flex flex-col h-full"
                >
                  <div className="p-5 md:p-6 flex flex-col h-full">
                    <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-xs font-semibold w-fit">
                      ⭐ FREE PLAN
                    </span>

                    <div className="mt-4 text-3xl md:text-4xl font-bold text-slate-900">
                      ₹ 0
                    </div>

                    <div className="mt-4 flex-1">
                      <p className="text-sm font-semibold text-slate-900">
                        What's included:
                      </p>
                      <ul className="mt-3 space-y-2 text-sm text-slate-700">
                        {[
                          "Free Search",
                          "Send first free 50 emails",
                          "Fully customized AI email templates",
                          "Live email support",
                          "No payment required",
                        ].map((item) => (
                          <li key={item} className="flex gap-2">
                            <span className="text-emerald-600">✓</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-auto pt-6">
                      <Button
                        size="lg"
                        onClick={scrollToSearch}
                        className="transition-all duration-300 transform hover:-translate-y-1 w-full rounded-md py-3 text-sm font-semibold bg-transparent border border-indigo-500 text-black hover:bg-transparent hover:text-black"
                      >
                        Start Free
                      </Button>
                      <p className="mt-2 text-center text-xs text-slate-500">
                        No credit card required
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* PRO PLAN */}
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  whileHover={{ scale: 1.03 }}
                  className="rounded-2xl border border-indigo-300 bg-white hover:shadow-lg transition flex flex-col h-full"
                >
                  <div className="p-5 md:p-6 flex flex-col h-full">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 text-indigo-700 px-3 py-1 text-xs font-semibold">
                        💎 PRO PLAN
                      </span>
                      <span className="text-xs font-semibold text-indigo-600">
                        Most Popular
                      </span>
                    </div>

                    <div className="mt-4 text-3xl md:text-4xl font-bold text-slate-900">
                      <span className="">
                        {" "}
                        {selectedPlan.currency === "USD" ? "$" : "₹"}{" "}
                      </span>
                      <span className="text-4xl font-extrabold text-gray-900 ml-1">
                        {selectedPlan.price}
                        <span className="text-lg">/Month</span>
                      </span>{" "}
                    </div>

                    <div className="mt-4 flex-1">
                      <p className="text-sm font-semibold text-slate-900">
                        What's included:
                      </p>
                      <ul className="mt-3 space-y-2 text-sm text-slate-700">
                        {selectedPlan.description.map((item) => (
                          <li key={item} className="flex gap-2">
                            <span className="text-indigo-600">✓</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-auto pt-6">
                      <Button
                        size="lg"
                        onClick={handleGetStarted}
                        className="mt-6 w-full transition-all duration-300 transform hover:-translate-y-1 rounded-md py-3 text-sm font-semibold text-white main-button"
                      >
                        Get Started
                      </Button>
                      <p className="mt-2 text-center text-xs text-slate-500">
                        No hidden charges
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* ENTERPRISE PLAN */}
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  whileHover={{ scale: 1.03 }}
                  className="rounded-2xl border border-indigo-300 bg-white hover:shadow-lg transition flex flex-col h-full"
                >
                  <div className="p-5 md:p-6 flex flex-col h-full">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 text-indigo-700 px-3 py-1 text-xs font-semibold">
                        💼 ENTERPRISE PLAN
                      </span>
                    </div>

                    <div className="mt-4 text-2xl md:text-2xl font-bold text-slate-900">
                      Custom Pricing
                    </div>

                    <div className="mt-4 flex-1">
                      <p className="text-sm font-semibold text-slate-900">
                        What's included:
                      </p>
                      <ul className="mt-3 space-y-2 text-sm text-slate-700">
                        {[
                          "Email ID and / or Mobile Numbers",
                          "In Excel Format",
                          "Data volume as per your need",
                          "Secure delivery & verified contacts",
                          "Data consulting & targeting help",
                          "Delivered within 24 to 48 hrs",
                          "₹ 5 per email contact",
                          "₹ 10 per mobile number",
                        ].map((item) => (
                          <li key={item} className="flex gap-2">
                            <span className="text-indigo-600">✓</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-auto pt-6">
                      <Button
                        size="lg"
                        onClick={() => navigate("/contact-us")}
                        className="w-full transition-all duration-300 transform hover:-translate-y-1 rounded-md py-3 text-sm font-semibold text-white main-button"
                      >
                        Contact Sales
                      </Button>
                      <p className="mt-2 text-center text-xs text-slate-500">
                        Get leads tailored to your business.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
          <IntegrationToolsWidget />
          <section className="relative overflow-hidden py-14 max-md:py-8 bg-gradient-to-r from-blue-600 to-indigo-700">
            {/* Background boxes */}
            <div className="absolute inset-0 pointer-events-none">
              {/* LEFT CLUSTER */}
              <div className="absolute top-10 left-6 max-md:-left-20 w-32 h-32 bg-white/10 max-md:bg-white/5 rounded-xl animate-float"></div>
              <div className="absolute top-44 left-20 max-md:-left-16 w-16 h-16 bg-white/15 max-md:bg-white/5 rounded-md animate-float-slow"></div>
              <div className="absolute bottom-28 left-10 max-md:-left-24 w-24 h-24 bg-white/10 max-md:bg-white/5 rounded-lg animate-float"></div>
              <div className="absolute bottom-10 left-28 max-md:-left-12 w-12 h-12 bg-white/15 max-md:bg-white/5 rounded-md animate-float-slow"></div>

              {/* CENTER CLUSTER (HIDDEN ON MOBILE) */}
              <div className="absolute top-20 left-1/2 -translate-x-1/2 w-28 h-28 bg-white/5 rounded-xl animate-float hidden md:block"></div>
              <div className="absolute top-56 left-1/2 -translate-x-24 w-16 h-16 bg-white/10 rounded-lg animate-float-slow hidden md:block"></div>
              <div className="absolute bottom-40 left-1/2 translate-x-20 w-20 h-20 bg-white/5 rounded-lg animate-float hidden md:block"></div>

              {/* RIGHT CLUSTER */}
              <div className="absolute top-12 right-10 max-md:-right-24 w-36 h-36 bg-white/10 max-md:bg-white/5 rounded-2xl animate-float"></div>
              <div className="absolute top-52 right-32 max-md:-right-16 w-18 h-18 bg-white/15 max-md:bg-white/5 rounded-md animate-float-slow"></div>
              <div className="absolute bottom-24 right-14 max-md:-right-24 w-28 h-28 bg-white/10 max-md:bg-white/5 rounded-xl animate-float"></div>
              <div className="absolute bottom-10 right-36 max-md:-right-12 w-14 h-14 bg-white/15 max-md:bg-white/5 rounded-md animate-float-slow"></div>
            </div>

            {/* CONTENT */}
            <div className="text-center relative z-10 max-md:px-4">
              {isAuthenticated ? (
                <>
                  <h2 className=" text-white mb-6">You're all set</h2>
                  <p className="text-xl text-blue-100 mb-10 max-md:mb-6 max-w-2xl mx-auto max-md:text-base">
                    Discover tools designed to help you create, manage, and
                    scale with ease.
                  </p>

                  <div className="flex relative  sm:flex-row gap-4 max-md:gap-2 justify-center items-center max-md:flex-row ">
                    <Button
                      className="bg-white text-blue-600 hover:bg-gray-100 px-16 max-md:px-12 py-4 text-[16px] max-md:text-[14px] rounded-md transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                      onClick={scrollToSearch}
                    >
                      Try it out
                    </Button>

                    <Button
                      variant="outline"
                      className="border-2 border-white text-blue-600 hover:bg-white hover:text-blue-600 max-md:px-7 px-12 py-4 text-[16px]  max-md:text-[14px] rounded-md transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                      onClick={() => navigate("/pricing")}
                    >
                      View Pricing
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className=" text-white mb-6">
                    Ready To Grow Your Business?
                  </h2>
                  <p className="text-xl text-blue-100 mb-10 max-md:mb-6 max-w-2xl mx-auto max-md:text-base">
                    Discover tools designed to help you create, manage, and
                    scale with ease.
                  </p>

                  <div className="flex relative  sm:flex-row gap-4 max-md:gap-2 justify-center items-center max-md:flex-row ">
                    <Button
                      className="bg-white text-blue-600 hover:bg-gray-100 px-16 max-md:px-12 py-4 text-[16px] max-md:text-[14px] rounded-md transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                      onClick={() => openAuthModal("signup")}
                    >
                      Sign up
                    </Button>

                    <Button
                      variant="outline"
                      className="border-2 border-white text-blue-600 hover:bg-white hover:text-blue-600 max-md:px-7 px-12 py-4 text-[16px]  max-md:text-[14px] rounded-md transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                      onClick={() => navigate("/pricing-plans")}
                    >
                      View Pricing
                    </Button>
                  </div>
                </>
              )}

              <p className="mt-6 text-blue-100 text-sm">
                No credit card required
              </p>
            </div>
          </section>
        </main>
        <Footer />
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          defaultTab={authModalTab}
        />

      </div>
    </SearchProvider>
  );
}
